import { Role } from '../enums/role';
import {
    AllocationBalanceRow,
    RedemptionLegRow,
    RedemptionLegSource,
    ParentBalanceApplicability,
    AllocationSnapshotFields,
    ParentBalanceSignalFields,
    readParentBalanceDisplay,
    resolveParentBalanceApplicability,
    toAllocationBalanceRows,
    toRedemptionLegRows,
} from './cashback-allocation-balance';
import { Bank } from './bank';
import { Deserializable } from './deserializable';
import { TransactionLog } from './transaction.log';

/**
 * One supplier allocation leg of an RMS cashback.
 *
 * <p>The snapshot fields arrive from {@link AllocationSnapshotFields}, confirmed by LP-5352. They
 * stay optional and nullable because absence is a real and common state, not a transitional one: a
 * leg that never mutated has no snapshot row. See `cashback-allocation-balance.ts` for the full
 * contract boundary.
 */
export interface CashbackAllocation extends AllocationSnapshotFields {
    executionId: number;
    userWalletId: number;
    supplierWalletId: number;
    supplierId: number;
    amount: number;
    status: string;
    createdAt: string;
    updatedAt: string;
    liskId: string;
    supplierName: string;
}

export class Transaction implements Deserializable, ParentBalanceSignalFields {
    /**
     * LP-5358: the supplier legs of an RMS cashback REDEMPTION.
     *
     * <p>Newly exposed on the admin API. Previously this existed only on the mobile DTO, so a
     * "Wallet (Cashback Redeemed)" transaction had nothing to render for the balances that actually
     * moved, and the detail view showed only dashes.
     */
    public supplierCashbacksUsed?: RedemptionLegSource[] | null;

    public fromUserId!: string;
    public toUserId!: string;
    public transactionCode!: string;
    public type!: string;
    public amount!: number;
    public fees!: number;
    public senderFee!: number;
    public receiverFee!: number;
    public status!: string;
    public cashbackFees!: number;
    public fromWalletId!: number;
    public toWalletId!: number;
    public date!: string;
    public description!: string;
    public fromUserName!: string;
    public toUserName!: string;
    public fromRole!: string;
    public toRole!: string;
    public fromMobileNumber!: string;
    public toMobileNumber!: string;
    public employeeNumber!: string;
    public transferTransactionId!: string;
    public permitTransactionId!: string;
    public isLisk = false;
    public profilePicture!: string;
    public received = false;
    public canRefund = false;
    public parentId!: string;
    public refundTxnCode!: string;
    public settlementRequestId!: string;
    public receivedAt!: string;
    public settleOn!: string;
    public senderFeePercentage!: number;
    public bankDetails = new Bank();
    public transactionLog: TransactionLog[] = [];
    public fromUserCode!: string;
    public toUserCode!: string;
    public enterpriseId!: string;
    public fromUserBalanceBefore!: string;
    public fromUserBalanceAfter!: string;
    public toUserBalanceBefore!: string;
    public toUserBalanceAfter!: string;

    /**
     * Explicit backend statement that this parent transaction carried a wallet-level financial
     * movement of its own, so its Sender/Receiver before/after pair is real.
     *
     * <p>The LP-5352 contract does not carry this field -- it expresses the same thing by leaving
     * the balances null instead. This therefore resolves to `unknown` for every transaction today,
     * which preserves the legacy rendering, while a null balance falls through to the unavailable
     * marker rather than a fabricated `0.00`. See `cashback-allocation-balance.ts` for why the
     * reader is kept rather than removed.
     */
    public parentWalletMovement?: boolean | null;
    public senderReferredBy!: string;
    public receiverReferredBy!: string;
    public processing = true;
    public isShopifyCashIn = false;
    public groupId!: string;
    public wholesaler!: string;

    public shopifyOrder!: {
        isCashOnDelivery: boolean;
        referralCode: string;
        referredBy: string;
        agent: {
            userRoleId: number;
            name: string;
        };
    };

    public agent!: {
        userCode: string;
        storeName: string;
        storeAddress: string;
    };

    public extras!: {
        settlementBeneficiaryRef: string;
        sagepayKey: string;
    };

    public packageCollection!: {
        orderNumber: string;
        status: string;
    };

    public cashback!: {
        id: number;
        amount: number;
        status: string;
        source: string;
        transactionCode: string;

        // Legacy cashback transaction IDs
        wavesTransactionId: string;
        liskTransactionId: string;
        blockchainTransactionId: string;

        // New RMS supplier allocation cashback
        rmsEarn: boolean;
        lisk: boolean;
        allocations: CashbackAllocation[];

        // Pickup cashback / legacy fields
        pickupCashback: number;
        wavesPickupCashbackTxId: string;
        pickupCashbackPercentage: number;
        isRefund: boolean;
        wavesCashbackFeeTxId: string;
        cashbackFees: number;
        CashbackFeePercentage: number;
        walletId: number;
    };

    constructor() { }

    public getPackageCollectionStatus(): string {
        return this.packageCollection?.status === 'Collected'
            ? 'Package Collected'
            : 'Pending Collection';
    }

    public isPackageCollection(): boolean {
        return !!this.packageCollection && Object.keys(this.packageCollection).length > 0;
    }

    public getCashbackAmount(): number {
        if (!this.cashback) {
            return 0;
        }

        if (
            this.cashback.transactionCode &&
            this.cashback.transactionCode !== this.transactionCode
        ) {
            return this.cashback.pickupCashback || 0;
        }

        return this.cashback.amount || 0;
    }

    public hasCashbackAllocations(): boolean {
        return !!this.cashback?.allocations?.length;
    }

    public getUniqueAllocationLiskIds(): string[] {
        return [
            ...new Set(
                (this.cashback?.allocations || [])
                    .map((allocation) => allocation.liskId)
                    .filter((liskId) => !!liskId),
            ),
        ];
    }

    getCashbackBlockchainUrl(): string | null {
        if (!this.cashback) {
            return null;
        }

        if (this.hasCashbackAllocations()) {
            const liskIds = this.getUniqueAllocationLiskIds();
            return liskIds.length === 1 ? liskIds[0] : null;
        }

        return (
            this.cashback.liskTransactionId ||
            this.cashback.wavesTransactionId ||
            this.cashback.blockchainTransactionId ||
            null
        );
    }

    /**
     * LP-5358: every allocation leg resolved for rendering, in the order the API sent them.
     *
     * <p>Order, membership and grouping are the backend's: legs are never merged, sorted or
     * de-duplicated here, including when several share one execution or one blockchain hash.
     */
    public getAllocationBalanceRows(): AllocationBalanceRow[] {
        return toAllocationBalanceRows(this.cashback?.allocations);
    }

    /**
     * LP-5358: true when this transaction is a cashback redemption carrying supplier legs.
     *
     * <p>Driven purely by the payload. The portal does not infer "this is a redemption" from the
     * transaction type string, because the backend attaches these legs only to a genuine redemption
     * with captured supplier splits — being told is more reliable than guessing from a label that
     * exists for display.
     */
    public hasRedemptionSupplierLegs(): boolean {
        return !!this.supplierCashbacksUsed?.length;
    }

    /** LP-5358: the redemption legs resolved for rendering, in the order the API sent them. */
    public getRedemptionLegRows(): RedemptionLegRow[] {
        return toRedemptionLegRows(this.supplierCashbacksUsed);
    }

    /**
     * Whether any leg carries an execution/batch reference, used to drop an entirely empty
     * Execution column rather than render a full column of unavailable markers.
     */
    public hasAllocationExecutionRefs(): boolean {
        return this.getAllocationBalanceRows().some((row) => row.executionRef !== null);
    }

    /** See {@link resolveParentBalanceApplicability} -- resolved from the backend signal alone. */
    public getParentBalanceApplicability(): ParentBalanceApplicability {
        return resolveParentBalanceApplicability(this);
    }

    /**
     * True only when the backend has explicitly stated this parent carried no wallet movement, so
     * showing its before/after pair would misrepresent accounting that happened per allocation.
     */
    public isParentBalanceNotApplicable(): boolean {
        return this.getParentBalanceApplicability() === 'not-applicable';
    }

    public getFromUserBalanceBeforeDisplay(): string | null {
        return readParentBalanceDisplay(this.fromUserBalanceBefore);
    }

    public getFromUserBalanceAfterDisplay(): string | null {
        return readParentBalanceDisplay(this.fromUserBalanceAfter);
    }

    public getToUserBalanceBeforeDisplay(): string | null {
        return readParentBalanceDisplay(this.toUserBalanceBefore);
    }

    public getToUserBalanceAfterDisplay(): string | null {
        return readParentBalanceDisplay(this.toUserBalanceAfter);
    }

    public getEntTxnFromUserBalanceBefore(): string {
        return this.fromRole === Role.enterprise ? this.fromUserBalanceBefore : '';
    }

    public getEntTxFromUserBalanceAfter(): string {
        return this.fromRole === Role.enterprise ? this.fromUserBalanceAfter : '';
    }

    public getEntTxToUserBalanceBefore(): string {
        return this.toRole === Role.enterprise ? this.toUserBalanceBefore : '';
    }

    public getEntTxToUserBalanceAfter(): string {
        return this.toRole === Role.enterprise ? this.toUserBalanceAfter : '';
    }

    public getToUserName(): string {
        return this.toRole === Role.enterprise_staff
            ? this.toUserName + ' (' + this.wholesaler + ')'
            : this.toUserName;
    }

    public deserialize(input: any): this {
        Object.assign(this, input);
        return this;
    }
}
